import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import { BaseAutoTracker } from '../../auto/BaseAutoTracker';
import { buildAutoSourceOrigin } from '../../auto/build-auto-source-origin';
import type {
  ConversionEventOrigin,
  ConversionEventType,
} from '../../contracts';
import type { TrackerFactory } from '../../tracks/TrackerFactory';
import { HistoryNavigationPatch } from './HistoryNavigationPatch';

export type UrlDetectorOptions = {
  readonly eventType?: ConversionEventType;
  readonly origin?: ConversionEventOrigin;
  readonly sourceName?: string;
  readonly fireOnce?: boolean;
};

export class UrlDetector extends BaseAutoTracker {
  private readonly factory: TrackerFactory;

  private readonly patterns: RegExp[];

  private readonly eventType: ConversionEventType | undefined;

  private readonly origin: ConversionEventOrigin;

  private readonly fireOnce: boolean;

  private hasFired = false;

  private unsubscribeHistory: (() => void) | null = null;

  constructor(
    context: PixelRuntimeContext,
    factory: TrackerFactory,
    patterns: string[] = [],
    options?: UrlDetectorOptions
  ) {
    super(context);
    this.factory = factory;
    this.patterns = patterns.map(pattern => new RegExp(pattern, 'i'));
    this.eventType = options?.eventType;
    this.fireOnce = options?.fireOnce === true;
    this.origin =
      options?.origin ??
      (options?.sourceName && options.eventType
        ? buildAutoSourceOrigin('url', options.eventType, options.sourceName)
        : 'auto:url');
  }

  get name(): string {
    return this.eventType ? `UrlDetector(${this.eventType})` : 'UrlDetector';
  }

  protected onInit(): void {
    this.hasFired = false;
    if (!this.isWindowAvailable()) return;

    this.checkUrl();
    this.addWindowListener('popstate', () => this.checkUrl());
    this.unsubscribeHistory = HistoryNavigationPatch.subscribe(() =>
      this.checkUrl()
    );
  }

  protected onDestroy(): void {
    this.unsubscribeHistory?.();
    this.unsubscribeHistory = null;
    this.hasFired = false;
  }

  private checkUrl(): void {
    if (!this.isWindowAvailable()) return;
    if (this.fireOnce && this.hasFired) return;

    const href = window.location.href;
    const path = window.location.pathname;

    for (const pattern of this.patterns) {
      if (pattern.test(href) || pattern.test(path)) {
        const type =
          this.eventType ??
          (/thank|success|order|obrigado/i.test(pattern.source)
            ? 'PURCHASE'
            : 'INITIATE_CHECKOUT');

        this.factory.trackAuto(
          type,
          {},
          {
            origin: this.origin,
          }
        );

        if (this.fireOnce) {
          this.hasFired = true;
        }
        return;
      }
    }
  }
}
